import type { StorefrontOrder } from '@erp/shared';
import { ArrowLeft, FileText, PackageCheck, Truck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Badge } from '../../components/shadcn/index.js';
import { Button, Card, EmptyState, Skeleton } from '../../components/ui/index.js';
import { customerApi } from '../../lib/customerApi.js';
import { formatDateTime } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

const DELIVERY_STEPS = ['pending', 'assigned', 'out_for_delivery', 'delivered'] as const;

/** One online order: lines, totals, and live delivery tracking when a delivery exists. */
export function PortalOrderDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation('portal');
  const { id } = useParams();
  const [order, setOrder] = useState<StorefrontOrder | null | 'error'>(null);

  useEffect(() => {
    void customerApi()
      .get<StorefrontOrder>(`/orders/${id}`)
      .then((r) => setOrder(r.data))
      .catch(() => setOrder('error'));
  }, [id]);

  if (order === null) {
    return (
      <Card className="flex flex-col gap-2 p-4">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </Card>
    );
  }
  if (order === 'error') {
    return <EmptyState title={t('orders.notFound', { defaultValue: 'Order not found.' })} />;
  }

  const money = (v: string): string => formatMoney(v, { locale: i18n.language, currency: order.currencyCode });
  const delivery = order.delivery ?? null;
  // The delivery charge is an item-less line — show it as a fee, not a product row.
  const itemLines = order.lines.filter((l) => l.itemId);
  const deliveryLine = order.lines.find((l) => !l.itemId) ?? null;
  const stepIdx = delivery ? DELIVERY_STEPS.indexOf(delivery.status as (typeof DELIVERY_STEPS)[number]) : -1;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <Link to="/portal/orders" className="inline-flex items-center gap-1 text-sm text-fg-muted transition hover:text-fg">
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
          {t('orders.back', { defaultValue: 'All orders' })}
        </Link>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-mono text-2xl font-semibold">{order.orderNumber}</h1>
        <Badge variant={order.status === 'fulfilled' ? 'success' : order.status === 'cancelled' ? 'secondary' : 'warning'}>
          {t(`orders.status.${order.status}`, { defaultValue: order.status })}
        </Badge>
        <span className="text-sm text-fg-muted">{order.date}</span>
        {/* The same printable paper staff open from the admin — the portal
            mints the public link server-side (ownership re-checked there). */}
        <Button
          variant="secondary"
          size="sm"
          className="ms-auto"
          onClick={() => {
            void customerApi()
              .get<{ url: string }>(`/orders/${id}/paper`)
              .then((r) => window.open(r.data.url, '_blank', 'noopener'))
              .catch(() => undefined);
          }}
        >
          <FileText className="me-1.5 h-4 w-4" aria-hidden />
          {t('paper.viewOrder', { defaultValue: 'View invoice' })}
        </Button>
      </div>

      {delivery && (
        <Card className="flex flex-col gap-3 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            {delivery.status === 'delivered' ? <PackageCheck className="h-4 w-4 text-success" aria-hidden /> : <Truck className="h-4 w-4 text-brand" aria-hidden />}
            {t('orders.delivery', { defaultValue: 'Delivery' })}
            {delivery.driverName && <span className="font-normal text-fg-muted">· {delivery.driverName}</span>}
          </div>
          {delivery.status === 'failed' ? (
            <p className="text-sm text-danger">{t('orders.deliveryFailed', { defaultValue: 'Delivery attempt failed — the store will contact you.' })}</p>
          ) : (
            <ol className="flex items-center gap-1">
              {DELIVERY_STEPS.map((s, i) => (
                <li key={s} className="flex flex-1 items-center gap-1">
                  <span className={`h-2 w-full rounded-full ${i <= stepIdx ? 'bg-brand' : 'bg-border'}`} title={t(`orders.deliveryStatus.${s}`, { defaultValue: s })} />
                </li>
              ))}
            </ol>
          )}
          <p className="text-xs text-fg-muted">
            {t(`orders.deliveryStatus.${delivery.status}`, { defaultValue: delivery.status })}
            {delivery.deliveredAt ? ` · ${formatDateTime(delivery.deliveredAt, { locale: i18n.language })}` : ''}
          </p>
        </Card>
      )}

      <Card className="overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-xs uppercase text-fg-muted">
              <th className="p-3 text-start">{t('orders.item', { defaultValue: 'Item' })}</th>
              <th className="p-3 text-end">{t('orders.qty', { defaultValue: 'Qty' })}</th>
              <th className="p-3 text-end">{t('columns.amount')}</th>
            </tr>
          </thead>
          <tbody>
            {itemLines.map((l, i) => (
              <tr key={i} className="border-b border-border/60 last:border-0">
                <td className="p-3">{l.description}</td>
                <td className="p-3 text-end font-mono tabular-nums">{Number(l.quantity)}</td>
                <td className="p-3 text-end font-mono tabular-nums">{money(l.lineTotal)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {deliveryLine && (
              <tr>
                <td colSpan={2} className="p-3 text-end text-fg-muted">{t('orders.delivery', { defaultValue: 'Delivery' })}</td>
                <td className="p-3 text-end font-mono tabular-nums text-fg-muted">{money(deliveryLine.lineTotal)}</td>
              </tr>
            )}
            {Number(order.discountTotal) > 0 && (
              <tr>
                <td colSpan={2} className="p-3 text-end text-fg-muted">{t('orders.discount', { defaultValue: 'Discount' })}</td>
                <td className="p-3 text-end font-mono tabular-nums text-fg-muted">−{money(order.discountTotal)}</td>
              </tr>
            )}
            {Number(order.taxTotal) > 0 && (
              <tr>
                <td colSpan={2} className="p-3 text-end text-fg-muted">{t('orders.tax', { defaultValue: 'Tax' })}</td>
                <td className="p-3 text-end font-mono tabular-nums text-fg-muted">{money(order.taxTotal)}</td>
              </tr>
            )}
            <tr>
              <td colSpan={2} className="p-3 text-end font-semibold">{t('orders.total', { defaultValue: 'Total' })}</td>
              <td className="p-3 text-end font-mono text-base font-bold tabular-nums">{money(order.total)}</td>
            </tr>
            {Number(order.cashbackRedeemed ?? 0) > 0 && (
              <>
                <tr>
                  <td colSpan={2} className="p-3 text-end text-success">{t('orders.cashbackUsed', { defaultValue: 'Paid from balance' })}</td>
                  <td className="p-3 text-end font-mono tabular-nums text-success">−{money(order.cashbackRedeemed ?? '0')}</td>
                </tr>
                <tr>
                  <td colSpan={2} className="p-3 text-end font-semibold">{t('orders.amountDue', { defaultValue: 'Amount due' })}</td>
                  <td className="p-3 text-end font-mono text-base font-bold tabular-nums">{money(String(Number(order.total) - Number(order.cashbackRedeemed ?? 0)))}</td>
                </tr>
              </>
            )}
          </tfoot>
        </table>
      </Card>
    </div>
  );
}
